import { Fragment, useState } from 'react';
import { Download, FileStack, Loader2, Plus, Trash2, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MediaPageShell } from './components/MediaPageShell';
import { ActionIconButton } from './components/ActionIconButton';
import {
  useBriefItemsQuery,
  useBriefsQuery,
  useCreateBriefMutation,
  useDeleteBriefMutation,
  useRemoveBriefItemMutation,
  useSetBriefStatusMutation,
} from '@/client/api/marketing/radar';
import type { ApiBrief, ApiBriefItem } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';

/**
 * Radar → Briefs, on real data.
 *
 * A brief collects triaged coverage into something sendable. Publishing is a
 * POST rather than a PATCH because `published_at` is server-set — nobody should
 * be able to backdate when a brief went out.
 *
 * Export is a real client-side CSV built from the brief's own items. The mock
 * raised a "CSV export stub" toast, which is the kind of button that gets
 * demoed as working and then discovered not to be.
 */

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

const periodLabel = (b: ApiBrief) => {
  if (!b.period_start && !b.period_end) return '—';
  return `${fmt(b.period_start)} → ${fmt(b.period_end)}`;
};

/** RFC 4180 quoting: a headline containing a comma must not split the row. */
const csvCell = (value: unknown) => {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function buildCsv(brief: ApiBrief, items: ApiBriefItem[]): string {
  const header = [
    'position',
    'title',
    'outlet',
    'channel',
    'kind',
    'media_class',
    'sentiment',
    'published_at',
    'url',
    'note',
  ];
  const rows = items.map((item, i) => {
    const c = item.coverage;
    return [
      item.position ?? i,
      // A deleted document still gets a row: the brief said something was here.
      c?.title ?? '(coverage no longer available)',
      c?.outlet_name ?? '',
      c?.channel ?? '',
      c?.kind ?? '',
      c?.media_class ?? '',
      c?.sentiment ?? '',
      c?.published_at ?? '',
      c?.url ?? '',
      item.note ?? '',
    ].map(csvCell);
  });
  return [header.join(','), ...rows.map((r) => r.join(','))].join('\n');
}

/** Expanded row: the coverage inside one brief. */
function BriefContents({ brief }: { brief: ApiBrief }) {
  const { data, isLoading, isError, error } = useBriefItemsQuery(brief.mbid);
  const removeItem = useRemoveBriefItemMutation();
  const items = data?.items ?? [];

  const download = () => {
    const csv = buildCsv(brief, items);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${brief.title.replace(/[^\w-]+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="px-6 py-4 text-xs text-gray-500">
        <Loader2 className="mr-2 inline h-3.5 w-3.5 animate-spin" />
        Loading contents…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="px-6 py-4 text-xs text-amber-800">
        {getApiErrorMessage(error, 'Could not load this brief’s contents')}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="px-6 py-4 text-xs text-gray-500">
        Nothing in this brief yet — triage coverage in Radar and add it from there.
      </div>
    );
  }

  return (
    <div className="space-y-2 px-6 py-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {items.length} item{items.length === 1 ? '' : 's'}
        </span>
        <Button type="button" size="sm" variant="outline" className="h-7 gap-1" onClick={download}>
          <Download className="h-3.5 w-3.5" /> CSV
        </Button>
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li
            key={item.mbiid}
            className="flex items-start justify-between gap-3 rounded-md border border-gray-100 px-3 py-2"
          >
            <div className="min-w-0">
              {item.coverage ? (
                <>
                  <p className="truncate text-sm text-gray-900">{item.coverage.title}</p>
                  <p className="truncate text-[11px] text-gray-500">
                    {item.coverage.outlet_name ?? item.coverage.site_name ?? item.coverage.channel}
                    {item.coverage.published_at
                      ? ` · ${fmt(item.coverage.published_at)}`
                      : ''}
                  </p>
                </>
              ) : (
                /* The link outlived the document. Showing the gap beats a brief
                   that silently shrinks between viewings. */
                <p className="text-sm italic text-amber-700">
                  Coverage no longer available ({item.coverage_id})
                </p>
              )}
            </div>
            <ActionIconButton
              label="Remove from brief"
              size="sm"
              onClick={() =>
                removeItem.mutate(
                  { mbid: brief.mbid, mbiid: item.mbiid },
                  {
                    onError: (err) =>
                      toast.error(getApiErrorMessage(err, 'Could not remove the item')),
                  },
                )
              }
              className="shrink-0 text-gray-400 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </ActionIconButton>
          </li>
        ))}
      </ul>
    </div>
  );
}

const MediaBriefsPage = () => {
  const { data, isLoading, isError, error } = useBriefsQuery({ limit: 100, with_counts: 1 });
  const createBrief = useCreateBriefMutation();
  const deleteBrief = useDeleteBriefMutation();
  const setStatus = useSetBriefStatusMutation();

  const [expanded, setExpanded] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [summary, setSummary] = useState('');

  const briefs: ApiBrief[] = data?.items ?? [];
  const stat = (n: number) => (isLoading ? '—' : String(n));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('A brief needs a title');
      return;
    }
    if (periodStart && periodEnd && periodEnd < periodStart) {
      toast.error('The period end must be on or after the start');
      return;
    }
    createBrief.mutate(
      {
        title: title.trim(),
        period_start: periodStart || null,
        period_end: periodEnd || null,
        summary: summary.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success(`Created “${title.trim()}”`);
          setTitle('');
          setPeriodStart('');
          setPeriodEnd('');
          setSummary('');
          setDialogOpen(false);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not create the brief')),
      },
    );
  };

  return (
    <MediaPageShell
      title="Briefs & clip books"
      description="Assemble reviewed coverage into digests for leadership and partners."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Radar', href: '/admin/marketing/radar' },
        { label: 'Briefs' },
      ]}
      icon={FileStack}
      actions={
        <Button type="button" className="gap-2" onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4" /> New brief
        </Button>
      }
      stats={[
        { label: 'Briefs', value: stat(briefs.length) },
        {
          label: 'Published',
          value: stat(briefs.filter((b) => b.status === 'published').length),
        },
        { label: 'Drafts', value: stat(briefs.filter((b) => b.status === 'draft').length) },
      ]}
    >
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <TooltipProvider delayDuration={200}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Brief', 'Period', 'Items', 'Status'].map((h) => (
                <th
                  key={h}
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {h}
                </th>
              ))}
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-6 py-16 text-center text-sm text-gray-500">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                  Loading briefs…
                </td>
              </tr>
            )}

            {!isLoading && isError && (
              <tr>
                <td colSpan={5} className="px-6 py-16 text-center text-sm">
                  <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
                  <div className="font-medium text-amber-800">Could not load briefs</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {getApiErrorMessage(error, 'The marketing API did not respond')}
                  </div>
                </td>
              </tr>
            )}

            {!isLoading && !isError && briefs.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-16 text-center text-sm text-gray-500">
                  No briefs yet — create one, then add triaged coverage to it from Radar.
                </td>
              </tr>
            )}

            {briefs.map((brief) => {
              const count = brief.item_count ?? 0;
              const isOpen = expanded === brief.mbid;
              return (
                <Fragment key={brief.mbid}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : brief.mbid)}
                        className="text-left text-sm font-medium text-gray-900 underline-offset-2 hover:underline"
                      >
                        {brief.title}
                      </button>
                      {brief.summary && (
                        <div className="mt-1 line-clamp-1 text-xs text-gray-500">
                          {brief.summary}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{periodLabel(brief)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {count === 0 ? (
                        <span className="text-amber-700">empty</span>
                      ) : (
                        count
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                          brief.status === 'published'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-amber-50 text-amber-800'
                        }`}
                        title={
                          brief.published_at
                            ? `Published ${new Date(brief.published_at).toLocaleString()}`
                            : undefined
                        }
                      >
                        {brief.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8"
                          disabled={brief.status === 'draft' && count === 0}
                          title={
                            brief.status === 'draft' && count === 0
                              ? 'Add coverage before publishing'
                              : undefined
                          }
                          onClick={() =>
                            setStatus.mutate(
                              { mbid: brief.mbid, published: brief.status !== 'published' },
                              {
                                onSuccess: () =>
                                  toast.success(
                                    brief.status === 'published'
                                      ? 'Brief moved back to draft'
                                      : 'Brief published',
                                  ),
                                onError: (err) =>
                                  toast.error(
                                    getApiErrorMessage(err, 'Could not change the status'),
                                  ),
                              },
                            )
                          }
                        >
                          {brief.status === 'published' ? 'Unpublish' : 'Publish'}
                        </Button>
                        <ActionIconButton
                          label="Delete"
                          onClick={() =>
                            deleteBrief.mutate(
                              { mbid: brief.mbid },
                              {
                                onSuccess: () => toast.success('Brief removed'),
                                onError: (err) =>
                                  toast.error(
                                    getApiErrorMessage(err, 'Could not remove the brief'),
                                  ),
                              },
                            )
                          }
                          className="border border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </ActionIconButton>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-gray-50/60">
                      <td colSpan={5} className="p-0">
                        <BriefContents brief={brief} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        </TooltipProvider>
        <div className="border-t border-gray-100 px-6 py-3 text-xs text-gray-400">
          Open a brief to see and export its coverage. Items are added from Radar → Coverage.
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New brief</DialogTitle>
            <DialogDescription>
              A brief starts empty. Add reviewed coverage to it from Radar, then publish.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Title</span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputClass}
                placeholder="August coverage — leadership digest"
                required
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Period start</span>
                <input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Period end</span>
                <input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className={inputClass}
                />
              </label>
            </div>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Summary</span>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                className="min-h-[72px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
                placeholder="What this period looked like, and what the reader should take from it."
              />
            </label>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={createBrief.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createBrief.isPending}>
                {createBrief.isPending ? 'Creating…' : 'Create brief'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </MediaPageShell>
  );
};

export default MediaBriefsPage;
